"use client";

import Image from "next/image";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { SearchableCombobox } from "@/components/ui/SearchableCombobox";
import { NAV_INK_FOCUS_RING } from "@/components/nav/nav-styles";
import { cn } from "@/lib/utils";
import logoOrange from "../../public/logo-orange.png";

// Past this many studios a plain <Select> is unusable, so the switcher becomes
// a searchable combobox instead.
const WORKSPACE_SEARCH_THRESHOLD = 10;

// The trigger is the top bar's logo with the pickers' chevron beside it, on
// the navy frame.
const TRIGGER = cn(
  "h-auto w-auto gap-2 rounded-lg border-0 bg-transparent px-1.5 py-1 text-navy-300 hover:bg-white/5 focus:ring-0 focus:ring-offset-0 [&>svg]:opacity-100",
  NAV_INK_FOCUS_RING,
);

// An uploaded logo on the navy frame: most are drawn for a light page, a third
// on a solid white JPEG background. Inverting lightness (hue turned back, so
// coral stays coral) and screening onto the navy makes that white background
// navy and dark lettering white, for transparent and solid logos alike.
const ON_INK = "invert hue-rotate-180 mix-blend-screen";

/**
 * The studio's logo: a 32px square when `compact` (in the navy bar, an
 * uploaded image up to the rail's width instead), otherwise 40px tall and
 * as wide as the image is, up to 15rem. A studio without an image shows the
 * Salesably mark, with the studio's name beside it at the wide size. `ink`
 * seats it on the navy top bar.
 */
export function WorkspaceLogo({ workspace, compact = false, ink = false }) {
  const alt = workspace?.name ? `${workspace.name} Logo` : "Salesably Logo";
  const uploaded = ink && Boolean(workspace?.image) && ON_INK;
  if (compact) {
    return (
      <Image
        src={workspace?.image || logoOrange}
        alt={alt}
        width={32}
        height={32}
        className={cn(
          "rounded-md object-contain",
          // Most uploads are wordmarks: in the bar, keep the image's own
          // shape across the rail's width rather than shrinking it to a square.
          uploaded ? "h-7 w-auto max-w-[4.5rem]" : "h-8 w-8",
          uploaded,
        )}
        priority
      />
    );
  }
  // In the navy bar the full mark is a step smaller than on a page, so it
  // keeps a margin inside the bar and is nearer the compact one beside an
  // open chat.
  if (workspace?.image) {
    return (
      <Image
        src={workspace.image}
        alt={alt}
        width={240}
        height={40}
        className={cn(
          "w-auto rounded-md object-contain",
          ink ? "h-8 max-w-48" : "h-10 max-w-60",
          uploaded,
        )}
        priority
      />
    );
  }
  return (
    <span className="flex items-center gap-2">
      <Image
        src={logoOrange}
        alt={alt}
        width={40}
        height={40}
        className={cn("object-contain", ink ? "h-8 w-8" : "h-10 w-10")}
        priority
      />
      <span
        className={cn(
          "truncate text-lg font-semibold",
          ink ? "text-white" : "text-gray-900",
        )}
      >
        {workspace?.name || "Select Studio"}
      </span>
    </span>
  );
}

export default function WorkspaceSwitcher({
  currentWorkspace,
  workspaces,
  value,
  onSelect,
  isSwitching,
}) {
  if (!workspaces || workspaces.length === 0) return null;

  const studioSwitcherLabel = currentWorkspace?.name
    ? `Switch studio. Current studio: ${currentWorkspace.name}`
    : "Select a studio";
  const logo = <WorkspaceLogo workspace={currentWorkspace} ink />;

  if (workspaces.length > WORKSPACE_SEARCH_THRESHOLD) {
    return (
      <SearchableCombobox
        items={workspaces}
        selectedValue={value || undefined}
        onSelect={(id) => {
          if (id === currentWorkspace?.id) return;
          const workspace = workspaces.find((w) => w.id === id);
          if (workspace) onSelect(workspace);
        }}
        placeholder="Select a studio..."
        searchPlaceholder="Search studios..."
        emptyMessage="No studios found."
        ariaLabel={studioSwitcherLabel}
        disabled={isSwitching}
        className={TRIGGER}
        popoverContentClassName="w-[22rem] max-w-[calc(100vw-2rem)]"
        renderSelected={() => logo}
      />
    );
  }

  return (
    <Select
      value={value || undefined}
      disabled={isSwitching}
      onValueChange={(nextValue) => {
        if (nextValue === currentWorkspace?.id) return;
        const workspace = workspaces.find((w) => w.id === nextValue);
        if (workspace) {
          onSelect(workspace);
        }
      }}
    >
      <SelectTrigger
        aria-label={studioSwitcherLabel}
        disabled={isSwitching}
        className={TRIGGER}
      >
        {logo}
      </SelectTrigger>
      <SelectContent>
        {workspaces.map((workspace) => (
          <SelectItem
            key={workspace.id}
            value={workspace.id}
            className="text-base text-gray-700"
          >
            {workspace.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
